import React, {
  createContext,
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

export const BG_PRIORITY = {
  GLOBAL: 0,
  PAGE: 1,
  COMPONENT: 2,
} as const;

interface BackgroundRegistration {
  id: string;
  color: string;
  priority: number;
}

interface BackgroundColorContextValue {
  register: (entry: BackgroundRegistration) => void;
  unregister: (id: string) => void;
}

const BackgroundColorContext = createContext<BackgroundColorContextValue>({
  register: () => {},
  unregister: () => {},
});

export const BackgroundColorProvider: FunctionComponent<{
  globalColor?: string;
}> = ({ children, globalColor }) => {
  const registrationsRef = useRef<BackgroundRegistration[]>([]);
  const globalColorRef = useRef(globalColor);
  globalColorRef.current = globalColor;

  const apply = useCallback(() => {
    const sorted = [...registrationsRef.current].sort(
      (a, b) => b.priority - a.priority
    );
    const color = sorted[0]?.color ?? globalColorRef.current;
    if (color) document.body.style.backgroundColor = color;
  }, []);

  useEffect(() => {
    apply();
  }, [globalColor, apply]);

  const register = useCallback(
    (entry: BackgroundRegistration) => {
      registrationsRef.current = [
        ...registrationsRef.current.filter((r) => r.id !== entry.id),
        entry,
      ];
      apply();
    },
    [apply]
  );

  const unregister = useCallback(
    (id: string) => {
      registrationsRef.current = registrationsRef.current.filter(
        (r) => r.id !== id
      );
      apply();
    },
    [apply]
  );

  return (
    <BackgroundColorContext.Provider value={{ register, unregister }}>
      {children}
    </BackgroundColorContext.Provider>
  );
};

export const usePageBackgroundColor = (
  color: string | undefined,
  priority: number
) => {
  const { register, unregister } = useContext(BackgroundColorContext);
  const id = useRef(`bg-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    if (!color) return;
    register({ id, color, priority });
    return () => unregister(id);
  }, [color, id, priority, register, unregister]);
};
